import { NonQuotaTargeting, Quota } from '@ps/survey-utils';
import {
  isChildAgeGenderQuota,
  toChildAgeGenderCriterion,
} from '@ps/survey-data-access';

import { FeasibilityDefaultQuotaCriteriaDto } from '../dtos/feasibility.dto';

import {
  FeasibilityQuotaRows,
  TargetedQuota,
  toQuotaSet,
  withResolvedUnitsForPayload,
} from '../domain';

import { FeasibilityDefaultQuotaPayloadMapper } from './feasibility-default-quota.payload.mapper';
import { FeasibilityQuotaCriteriaPayloadMapper } from './feasibility-quota-criteria.payload.mapper';

export interface FeasibilityQuotaPayloadResult extends FeasibilityQuotaRows {
  readonly defaultQuotaCriteria: FeasibilityDefaultQuotaCriteriaDto[];
}

/**
 * Survey quotas + the default criteria bucket -> the API's `quotas` and
 * `defaultQuota` criteria, plus the drawer-facing `quotaSet`.
 */
export class FeasibilityQuotaPayloadMapper {
  constructor(
    private readonly _quotas: readonly Quota[],
    private readonly _defaultQuotaCriteria: readonly FeasibilityDefaultQuotaCriteriaDto[],
    private readonly _completesRequired: number,
    private readonly _childAgeGenderConditionNameByCode: ReadonlyMap<
      string,
      string
    > = new Map(),
  ) {}

  public map(): FeasibilityQuotaPayloadResult {
    const targetedQuotas = this._targetedQuotas();
    const targeting = NonQuotaTargeting.from(
      targetedQuotas.map((quota) => quota.qualificationCodes()),
    );
    const defaultCriteria = this._defaultQuotaCriteria.map((criterion) =>
      withResolvedUnitsForPayload(criterion),
    );
    const criteriaMapper = new FeasibilityQuotaCriteriaPayloadMapper(
      defaultCriteria,
      targeting,
    );

    return {
      ...(targetedQuotas.length
        ? this._rowsFromQuotas(targetedQuotas, criteriaMapper)
        : new FeasibilityDefaultQuotaPayloadMapper(
            defaultCriteria,
            criteriaMapper,
            this._completesRequired,
          ).map()),
      defaultQuotaCriteria: defaultCriteria.map((criterion) =>
        this._taggedDefaultCriterion(criterion, targeting),
      ),
    };
  }

  private _targetedQuotas(): readonly TargetedQuota[] {
    return this._quotas
      .flatMap((quota) => this._childAgeGenderConverted(quota))
      .flatMap((quota) =>
        TargetedQuota.from(quota).mapOr([], (targeted) => [targeted]),
      );
  }

  // A children (218) quota row is converted to its real 232 wire representation here, same as
  // SurveyQuotaPayloadMapper does for the survey-save payload — feasibility must see the same
  // age/gender-expanded condition codes the backend actually persists, not the drawer's merged
  // 218 shape. An incomplete row (no gender/range chosen yet) is dropped, matching
  // toChildAgeGenderCriterion's contract.
  private _childAgeGenderConverted(quota: Quota): readonly Quota[] {
    if (!isChildAgeGenderQuota(quota)) {
      return [quota];
    }
    return toChildAgeGenderCriterion(
      quota,
      this._childAgeGenderConditionNameByCode,
    ).match({
      ok: ({ criterion }) => [{ ...quota, criteria: [criterion] }],
      err: () => [],
    });
  }

  private _rowsFromQuotas(
    targetedQuotas: readonly TargetedQuota[],
    criteriaMapper: FeasibilityQuotaCriteriaPayloadMapper,
  ): FeasibilityQuotaRows {
    return {
      quotas: targetedQuotas.map((quota) =>
        quota.toPayloadRow(criteriaMapper.mapForQuota(quota.ownCriteria())),
      ),
      quotaSet: toQuotaSet(targetedQuotas),
    };
  }

  /** None means the criterion states no verdict, so only the key is omitted — not the criterion. */
  private _taggedDefaultCriterion(
    criterion: FeasibilityDefaultQuotaCriteriaDto,
    targeting: NonQuotaTargeting,
  ): FeasibilityDefaultQuotaCriteriaDto {
    return targeting
      .flagForDefaultCriterion(criterion.qualification_code)
      .mapOr(criterion, (nonQuotaTargeted) => ({
        ...criterion,
        non_quota_targeted: nonQuotaTargeted,
      }));
  }
}
